import { createContext, type ReactNode, useEffect, useState } from 'react'
import { ShortcutsDialog } from '@/modules/shortcuts/components/shortcuts-dialog'
import {
	createDispatcher,
	createRegistry,
	type KeyContext,
	type Registry
} from '@/modules/shortcuts/lib/dispatcher'
import { currentPlatform } from '@/modules/shortcuts/lib/shortcuts'

export const ShortcutsContext = createContext<Registry | null>(null)

/**
 * The one `keydown` listener the client's shortcuts go through.
 *
 * **One listener, many owners.** A component that owns an action registers it
 * with `useShortcut` while it is mounted; this file decides, in one place,
 * whether a key press is anybody's — the guards for text fields and dialogs,
 * and the half-typed sequence — instead of every owner repeating them.
 *
 * **It stands aside for anything that already took the key.** A handler that
 * called `preventDefault` — a menu closing on `Escape`, the listing moving on
 * an arrow — has answered it.
 *
 * The registry is lazy state, not a memo: its identity has to survive every
 * render, because every `useShortcut` below subscribes through it.
 */
export function ShortcutsProvider({ children }: { children: ReactNode }) {
	const [registry] = useState(createRegistry)
	const [helpOpen, setHelpOpen] = useState(false)

	useEffect(() => {
		const dispatch = createDispatcher(
			currentPlatform(),
			(id) => registry.handler(id) !== undefined
		)

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.defaultPrevented || event.isComposing) {
				return
			}

			const result = dispatch(
				event,
				describeTarget(event.target),
				event.timeStamp
			)

			if (result.type === 'none') {
				return
			}

			event.preventDefault()

			if (result.type === 'run') {
				registry.handler(result.id)?.(event)
			}
		}

		window.addEventListener('keydown', onKeyDown)

		return () => window.removeEventListener('keydown', onKeyDown)
	}, [registry])

	useEffect(
		() => registry.register('shortcuts.help', () => setHelpOpen(true)),
		[registry]
	)

	return (
		<ShortcutsContext.Provider value={registry}>
			{children}
			<ShortcutsDialog onOpenChange={setHelpOpen} open={helpOpen} />
		</ShortcutsContext.Provider>
	)
}

function describeTarget(target: EventTarget | null): KeyContext {
	const element = target instanceof HTMLElement ? target : null

	return {
		editable:
			element !== null &&
			(element.isContentEditable ||
				['INPUT', 'SELECT', 'TEXTAREA'].includes(element.tagName)),
		inDialog:
			element?.closest('[role="dialog"], [role="alertdialog"]') != null,
		textSelected: !(window.getSelection()?.isCollapsed ?? true),
		// A key press with nothing focused arrives on the body.
		unfocused: element === null || element === document.body
	}
}
